import { useMemo, useState } from 'react';
import { Button, Card, Field, Pill, Select, TextInput } from '@salonomy/ui';
import {
  useCommissionRules,
  useDeleteCommissionRule,
  useSetCommissionRule,
} from '../../data/commission.js';
import { useServices } from '../../data/services.js';
import { useStaff } from '../../data/salon.js';
import { ApiClientError } from '../../lib/api.js';
import { percentFromBps } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/**
 * WS9 §4 — per-staff commission RULES: a default rate plus optional per-service-CATEGORY overrides
 * (`category` null = default rate; a category string = the override). Rates are basis points server-side;
 * the form takes a % and the list shows a % HONESTLY (via percentFromBps), but the value POSTed is bps.
 *
 * Re-setting the same (staff, category) REPLACES the rate (a current-state upsert, not a ledger). The
 * server re-validates; conflicts/validation surface faithfully through the toast. Submit is gated on
 * isPending so a double-tap can't double-post.
 *
 * MONEY HONESTY: no money is computed here — rules are CONFIG. Earnings (£) are the server's, shown by
 * EarningsSection over the same report range. Manager-gated: a non-manager sees an honest note and no
 * mutations fire.
 */

/** "20" | "17.5" → 2000 / 1750 bps; null if not a valid 0–100 rate. */
function percentToBps(input: string): number | null {
  const cleaned = input.replace(/[%\s]/g, '');
  if (!cleaned) return null;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0 || value > 100) return null;
  return Math.round(value * 100);
}

export function CommissionRatesPanel({ isFloorManager }: { isFloorManager: boolean }) {
  // Manager-gate at the panel boundary so the (RLS-floor) rules list is never even fetched for a
  // non-manager — the honest note carries no read, so no 403 is ever guaranteed.
  if (!isFloorManager) {
    return (
      <Card>
        <div className="section-title">Commission rates</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Setting commission rates is available to salon owners and managers. Your own effective
          rate and earnings appear with your KPIs.
        </div>
      </Card>
    );
  }
  return <CommissionRatesBody />;
}

function CommissionRatesBody() {
  const toast = useToast();
  const { data: staff = [] } = useStaff();
  const { data: services = [] } = useServices();
  const { data: rules = [], isLoading } = useCommissionRules();
  const setRule = useSetCommissionRule();
  const deleteRule = useDeleteCommissionRule();

  const [staffId, setStaffId] = useState('');
  const [category, setCategory] = useState(''); // '' = default rate
  const [percent, setPercent] = useState('');

  // The team members commission can be paid to (employed staff + renters who take work).
  const payableStaff = useMemo(
    () =>
      staff
        .filter((s) => s.active && ['owner', 'stylist', 'chair_renter'].includes(s.role))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [staff],
  );

  // Distinct service categories for the override picker (free-text category string on a Service).
  const categories = useMemo(
    () =>
      Array.from(
        new Set(services.map((s) => s.category).filter((c): c is string => Boolean(c))),
      ).sort((a, b) => a.localeCompare(b)),
    [services],
  );

  const staffName = (id: string): string =>
    payableStaff.find((s) => s.id === id)?.displayName ?? 'Staff member';

  async function save() {
    if (!staffId) {
      toast.error('Pick a staff member.');
      return;
    }
    const rateBps = percentToBps(percent);
    if (rateBps == null) {
      toast.error('Enter a commission rate between 0% and 100%.');
      return;
    }
    try {
      await setRule.mutateAsync({
        staffId,
        category: category.trim() ? category.trim() : null,
        rateBps,
      });
      toast.success('Commission rule saved');
      setPercent('');
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.message : 'Could not save the commission rule.',
      );
    }
  }

  async function remove(id: string) {
    try {
      await deleteRule.mutateAsync(id);
      toast.success('Commission rule removed');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove the rule.');
    }
  }

  // Group rules by staff member: a default (category null) + any per-category overrides.
  const byStaff = new Map<
    string,
    { defaultRule?: (typeof rules)[number]; overrides: typeof rules }
  >();
  for (const r of rules) {
    const entry = byStaff.get(r.staffId) ?? { overrides: [] };
    if (r.category == null) entry.defaultRule = r;
    else entry.overrides.push(r);
    byStaff.set(r.staffId, entry);
  }

  return (
    <Card>
      <div className="section-title">Commission rates</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        A default rate per staff member, plus optional per-category overrides. Rates are recorded as
        a percentage; commission and earnings (£) are computed by the server over recognised revenue
        — never voucher or gift-card sales.
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1.4fr 0.9fr auto',
          gap: 12,
          marginTop: 12,
          alignItems: 'end',
        }}
      >
        <Field label="Staff member">
          <Select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            <option value="">Select…</option>
            {payableStaff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Applies to">
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Default rate (all services)</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Rate (%)">
          <TextInput
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="20"
          />
        </Field>
        <Button onClick={save} disabled={setRule.isPending}>
          {setRule.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          Loading commission rules…
        </div>
      ) : byStaff.size === 0 ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          No commission rules yet. A staff member with no rule earns at the salon’s default.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 16 }}>
          {[...byStaff.entries()].map(([sid, entry]) => (
            <div key={sid} className="settings-row" style={{ alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{staffName(sid)}</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                  {entry.defaultRule
                    ? `Default ${percentFromBps(entry.defaultRule.rateBps)}`
                    : 'No default rate (salon default applies)'}
                </div>
                {entry.overrides.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {entry.overrides.map((o) => (
                      <Pill key={o.id} tone="soft">
                        {o.category}: {percentFromBps(o.rateBps)}
                      </Pill>
                    ))}
                  </div>
                ) : null}
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}
              >
                {entry.defaultRule ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={deleteRule.isPending}
                    onClick={() => remove(entry.defaultRule!.id)}
                  >
                    Remove default
                  </Button>
                ) : null}
                {entry.overrides.map((o) => (
                  <Button
                    key={o.id}
                    variant="ghost"
                    size="sm"
                    disabled={deleteRule.isPending}
                    onClick={() => remove(o.id)}
                  >
                    Remove {o.category}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
